import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultManagingInheritanceData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'managing-recent-inheritance-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: "You've just inherited money - what now?",
                variant: 'h4',
              },
            },
            {
              id: 'managing-recent-inheritance-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "Whether it's a modest sum or a substantial fortune, an inheritance can be both a once in a lifetime opportunity and an overwhelming experience.\n\nWhile the possibilities seem endless, it's important to make informed decisions to ensure your newfound wealth serves you well both now and in the future. Following the steps below can help you navigate this financial journey responsibly.",
              },
            },
          ],
        },
      },
      {
        id: 'left-column',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'item-1',
              type: 'PresentationListItem',
              props: {
                title: 'Take a moment before making decisions',
                description:
                  "Inheriting money often accompanies emotional circumstances. Whether it follows the loss of a loved one or comes from a surprise legacy, it's crucial to give yourself time to grieve and reflect. Rushing into financial decisions during an emotionally-charged period can lead to mistakes.",
                iconType: 'number',
                number: 1,
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-2',
              type: 'PresentationListItem',
              props: {
                title: 'Conduct a financial health check-up',
                description:
                  "Before deciding how to allocate your inheritance, assess your current financial situation. Review your liabilities, assets, income, and expenses. You'll be better equipped to make informed choices if you know your financial health.",
                iconType: 'number',
                number: 2,
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-3',
              type: 'PresentationListItem',
              props: {
                title: 'Seek professional guidance',
                description:
                  'The world of finance can be complex, and professional advice can be invaluable. Consider consulting a financial planner or advisor to create a tailored strategy based on your goals. They can help with investment strategies, tax implications, budgeting, and inheritance laws.',
                iconType: 'number',
                number: 3,
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-4',
              type: 'PresentationListItem',
              props: {
                title: 'Understand tax implications',
                description:
                  "Inheritances can have various tax implications, which depend on your location, the size of the inheritance, and how it's structured. Work with a tax advisor to understand any obligations and explore legal avenues to minimize your tax burden.",
                iconType: 'number',
                number: 4,
                iconColor: 'PRIMARY',
              },
            },
          ],
        },
      },
      {
        id: 'right-column',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'right-column-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'Additional actions to consider',
                variant: 'h4',
              },
            },
            {
              id: 'item-debt',
              type: 'PresentationListItem',
              props: {
                title: 'Pay off high-interest debt',
                description:
                  'If you have high-interest debts like credit cards or personal loans, prioritize paying them off. Reducing or eliminating these liabilities can free up more of your income for savings and investments.',
                iconType: 'lightbulb',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-emergency',
              type: 'PresentationListItem',
              props: {
                title: 'Establish an emergency fund',
                description:
                  "Set aside a portion of your inheritance in a readily accessible emergency fund. Financial advisors typically recommend three to six months' worth of living expenses. This fund will provide peace of mind and financial security if unexpected expenses arise.",
                iconType: 'lightbulb',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-invest',
              type: 'PresentationListItem',
              props: {
                title: 'Invest wisely',
                description:
                  'To grow your wealth over time, we recommend investing in a diversified portfolio that aligns with your risk tolerance and financial goals. Diversification can help mitigate risks and increase potential returns. Explore options such as stocks, bonds, real estate, or mutual funds.',
                iconType: 'lightbulb',
                iconColor: 'PRIMARY',
              },
            },
          ],
        },
      },
    ],
  };
}
